import { ResultType } from '@compumark/analyst-selection-tool-client';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root',
})
export class CellRendererService {
  constructor(private sanitizer: DomSanitizer) {}

  getRiskLevel(params: any): string {
    const value = params.value ? params.value[0] : '';
    return '<span class="risk-level ' + value + '">' + value + '</span>';
  }

  getGoodsMatch(params: any): string {
    let value = '';
    if (params.data.goodsAndServicesRisk?.level) {
      value =
        params.data.goodsAndServicesRisk.level === 'EXACT'
          ? 'E'
          : params.data.goodsAndServicesRisk.level === 'NONE'
          ? ''
          : '';
    }
    return '<span class="risk-level ' + value + '">' + value + '</span>';
  }

  getClasses(params: any, deliveryClasses: any): string {
    let formattedText = '';
    let classes = {
      primary: [],
      secondary: [],
      other: [],
    };
    if (deliveryClasses) {
      classes = deliveryClasses;
    }
    if (params.value) {
      for (const currentValue of params.value) {
        const normalizedValue = this.normalizeValue(currentValue);

        if (
          classes.primary?.some(
            (item) => this.normalizeValue(item) === normalizedValue,
          )
        ) {
          formattedText += `<b>${currentValue}</b>, `;
        } else if (
          classes.secondary?.some(
            (item) => this.normalizeValue(item) === normalizedValue,
          )
        ) {
          formattedText += `<b><i>${currentValue}</i></b>&#8201;, `;
        } else {
          formattedText += `${currentValue}, `;
        }
      }
    }

    return formattedText.trim().slice(0, -1);
  }

  private normalizeValue(value: string): string {
    return value.padStart(2, '0');
  }

  getTrademarkName(params: any): string {
    return this.createHighlightedHTMLContent(
      params,
      'highlightedTrademarkName',
      'highlightedTranslation',
      'TR',
    );
  }

  getCompanyName(params: any): string {
    return this.createHighlightedHTMLContent(
      params,
      'highlightedTrademarkName',
      'highlightedSecondaryName',
      'SEC',
    );
  }

  private getFormattedName(
    params: any,
    primaryKey: string,
    secondaryKey: string,
    secondaryLabel: string,
  ): string {
    const primaryValue = params.data[primaryKey];
    const secondaryValue = params.data[secondaryKey];

    if (!primaryValue) {
      return params.value;
    }

    const html: any = this.sanitizer.bypassSecurityTrustHtml(
      secondaryValue
        ? `${primaryValue}<br><strong>${secondaryLabel} - </strong>[${secondaryValue}]`
        : primaryValue,
    );
    return html ? html.changingThisBreaksApplicationSecurity : '';
  }
  getStatus(params: any): string {
    const status = params.value;
    const activeStatuses = ['PENDING', 'REGISTERED'];
    return `<span class="statuses ${
      activeStatuses.includes(status!) ? 'activeStatusPill' : ''
    }"> ${status}</span>`;
  }

  private getHighlightClass(params: any): string {
    return params.data.cited ? 'tr-name cited-highlight' : 'tr-name';
  }

  private createHighlightedHTMLContent(
    params: any,
    primaryKey: string,
    secondaryKey: string,
    secondaryLabel: string,
  ): string {
    const highlightClass = this.getHighlightClass(params);
    let trademarkName = `<span class="${highlightClass}">${this.getFormattedName(
      params,
      primaryKey,
      secondaryKey,
      secondaryLabel,
    )}</span>`;

    if (params.data.resultType === ResultType.DESIGN_ONLY) {
      trademarkName =
        trademarkName +
        `</br><span class="design-only"><i>[Design only]</i></span>`;
    }
    return trademarkName;
  }

  getStatistics(params: any): string {
    let formattedText = '';
    if (params.data.resultCountStats?.totalResults ?? 0 > 0) {
      const {
        totalResults,
        hiddenResults = 0,
        ttabResults = 0,
      } = params.data.resultCountStats;
      const totalCount = totalResults - hiddenResults - ttabResults;

      formattedText += `<div class="statistics-container"> <p class="totalCount"><label class="statistics-count-label">Total: </label> ${totalCount} results</p>`;
      if (params.data.resultCountStats?.trademarkResults > 0) {
        formattedText += `<p class="trademarkCount"><label class="statistics-count-label">TM:</label> ${
          params.data.resultCountStats?.trademarkResults - ttabResults
        } results</p>`;
      }
      formattedText += `</div>`;
    }

    return formattedText;
  }
}
